/**
 * The elevation field, and where the sea sits in it.
 *
 * Three contributions, in order of scale:
 *   1. Plate base height — decides where continents are at all.
 *   2. Warped fractal noise — gives coastlines their shape and inland variety.
 *   3. Boundary uplift — puts mountain chains where plates meet.
 *
 * Sea level is then found as a QUANTILE of the resulting heights rather than used as
 * a fixed threshold. This matters more than it sounds: with a fixed threshold, one
 * seed gives a waterworld and the next gives a supercontinent, because the noise
 * field's overall height drifts from seed to seed. Solving for the height that
 * produces the requested water fraction makes every seed playable.
 */

import type { RngState } from '../rng/index.js';
import { createNoise, warpedCylindricalFbm } from './noise.js';
import type { MapgenParams } from './params.js';
import { generatePlates, type PlateField } from './plates.js';

export interface ElevationField {
  /** Continuous height in [0, 1]. */
  height: Float32Array;
  /** The height that separates land from water. */
  seaLevel: number;
  plates: PlateField;
}

/**
 * Fraction of the way from the equator to the pole, 0 at the equator and 1 at the
 * pole. Used by climate too, so it lives here rather than being re-derived.
 */
export function latitudeOf(row: number, height: number): number {
  const middle = (height - 1) / 2;
  return Math.abs(row - middle) / middle;
}

/**
 * The value at the given quantile of a data set, via a sorted copy.
 * Exact rather than sampled: 12,600 floats sort in well under a millisecond, and an
 * approximate sea level would reintroduce exactly the seed-to-seed drift we are
 * trying to remove.
 */
export function quantile(values: Float32Array, fraction: number): number {
  if (values.length === 0) throw new Error('quantile() of an empty set');
  const sorted = Float32Array.from(values).sort();
  const position = Math.min(sorted.length - 1, Math.max(0, Math.round(fraction * (sorted.length - 1))));
  return sorted[position]!;
}

export function buildElevation(
  width: number,
  height: number,
  wrapX: boolean,
  params: MapgenParams,
  rng: RngState,
): ElevationField {
  const plates = generatePlates(width, height, wrapX, params, rng);
  const terrainNoise = createNoise(rng);
  const warpNoise = createNoise(rng);

  const tiles = width * height;
  const field = new Float32Array(tiles);

  for (let row = 0; row < height; row++) {
    // Poles are forced down so the map always ends in water. Without this, a
    // continent can run into the array boundary and every rule that looks at a
    // neighbour has to special-case the top and bottom rows forever.
    const latitude = latitudeOf(row, height);
    const polarBand = params.polarRows / height;
    const polarFalloff = latitude > 1 - polarBand * 2 ? Math.max(0, (1 - latitude) / (polarBand * 2)) : 1;

    for (let col = 0; col < width; col++) {
      const index = row * width + col;

      // Noise arrives in roughly [-1, 1]; shift it into [0, 1].
      const raw = warpedCylindricalFbm(
        terrainNoise,
        warpNoise,
        col,
        row,
        width,
        params.continentScale,
        params.warpStrength,
        { octaves: params.octaves, gain: params.gain },
      );
      const noise = raw * 0.5 + 0.5;

      // The plate BIASES the noise rather than being averaged with it. Averaging lets
      // the plate's flat value dominate, which flattens the noise's texture and makes
      // the Voronoi cell itself visible as a polygon. Adding a signed offset instead
      // shifts a whole region up or down while leaving its coastline detail intact.
      const plateBias = (plates.base[index]! - 0.5) * params.plateInfluence;
      const blended = Math.min(1, Math.max(0, noise + plateBias));

      // Uplift amplifies ground that is already high rather than raising new land out
      // of deep ocean; rifts (negative uplift) bite hardest where the crust is thin.
      const tectonic = plates.uplift[index]!;
      const uplift = tectonic >= 0 ? tectonic * (0.25 + 0.75 * blended) : tectonic * (1 - 0.5 * blended);

      field[index] = Math.min(1, Math.max(0, (blended + uplift) * polarFalloff));
    }
  }

  return { height: field, seaLevel: quantile(field, params.seaLevel), plates };
}
